import { ChipProps } from '@mui/material';
import moment from 'moment';
import { isEmpty } from 'lodash';

import { TQuizScoreStatus } from '../store/QuizSlice';
import { Countries, ISelectedOption, TP_COLORS } from '../constants';
import { TCourseStatus } from '../store/V2/CoursesSlice';
import { AdminCoursesFiltersData } from './LocalStorage';

export type TClassJoinStatusData = {
  status: string;
  color?: string;
  chipBg?: string;
  chipColor?: string;
};

export const constructOptionsFromArray = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  list: Array<any> = [],
  labelKey = 'name',
  valueKey = 'id',
  extraKeys?: string[]
): ISelectedOption[] => {
  const newList: ISelectedOption[] = [];

  if (list && list.length) {
    list.forEach((listItem, index) => {
      const item: ISelectedOption = {
        index,
        label: `${listItem[labelKey]}`,
        value: listItem[valueKey],
      };

      if (extraKeys && extraKeys.length) {
        extraKeys.forEach((extraKey) => {
          item[extraKey] = listItem[extraKey];
        });
      }

      newList.push(item);
    });
  }

  return newList;
};

export const getUrlConvertedString = (text: string): string =>
  (text || '').toLowerCase().trim().replace(/\//g, '-').replace(/ /g, '-');

export const isUrlValid = (str: string): boolean => {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i'
  ); // fragment locator

  return !!pattern.test(str);
};

export const getYouTubeVideoIdFromUrl = (url: string): string => {
  return (url.split('/').reverse()[0] || '').trim();
};

export const getYouTubeEmbedUrlByVideoId = (videoId: string): string => {
  return `${process.env.REACT_APP_YOUTUBE_EMBED_BASE_URL}/${videoId}`;
};

export const getYoutubeVideoImageThumbnailByVideoId = (
  videoId: string,
  type: 'default' | 'hqdefault' | 'mqdefault' | 'maxresdefault' = 'default'
): string => {
  return `${process.env.REACT_APP_YOUTUBE_THUMB_CONVERTER_BASE_URL}/${videoId}/${type}.jpg`;
};

export const getYouTubeEmbedUrlByLink = (link: string): string => {
  const videoId = getYouTubeVideoIdFromUrl(link);

  return getYouTubeEmbedUrlByVideoId(videoId);
};

export const getCountry = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : 'Asia/Singapore';
};

export const getCountryNameByTimezone = (timezone: string): string => {
  const matchingItem = Countries.find((item) => item.value === timezone);

  return matchingItem ? matchingItem.countryName : '';
};

export const getSubjectIconName = (subject: string): string => {
  switch (subject) {
    case 'Mathematics':
    case 'MathematicsNew':
      return 'maths';
    case 'Biology':
    case 'Biology A (Salters-Nuffield)':
    case 'Biology B':
      return 'biology';
    case 'Chemistry':
      return 'chemistry';
    case 'Physics':
      return 'physics';
    case 'Coordinated Science':
      return 'coordinated-science';
    case 'Economics':
      return 'economics';
    case 'Business Studies':
      return 'business-studies';
    case 'English Language':
      return 'english-language';
    case 'English Literature':
      return 'english-literature';
    case 'Additional Mathematics':
      return 'additional-maths';
    case 'Computer Science':
      return 'computer-science';
    case 'Combined Science':
      return 'combined-science';
    case 'Science':
      return 'science';
    case 'Chinese':
      return 'chinese';
    default:
      return 'maths';
  }
};

export const getQuizScoreStatus = (
  score: number,
  totalScore: number
): TQuizScoreStatus => {
  const percentage = (score / totalScore) * 100;

  let status: TQuizScoreStatus;

  if (percentage < 60) {
    status = 'Revise';
  } else if (percentage < 90) {
    status = 'Good';
  } else {
    status = 'Very Good';
  }

  return status;
};

export const getQuizScoreLabelColor = (
  quizScoreStatus: TQuizScoreStatus
): {
  chipType: ChipProps['color'];
  backgroundColor?: string;
  color?: string;
} => {
  let colorData: {
    chipType: ChipProps['color'];
    backgroundColor?: string;
    color?: string;
  } = {
    chipType: 'default',
  };

  switch (quizScoreStatus) {
    case 'Revise':
      colorData = {
        chipType: 'error',
        backgroundColor: '#FF5630',
        color: TP_COLORS.white,
      };
      break;

    case 'Good':
      colorData = {
        chipType: 'warning',
        backgroundColor: TP_COLORS.yellow.light,
        color: TP_COLORS.white,
      };
      break;

    case 'Very Good':
      colorData = {
        chipType: 'success',
        backgroundColor: '#22C55E',
        color: TP_COLORS.white,
      };
      break;

    default:
      colorData = {
        chipType: 'default',
      };
      break;
  }

  return colorData;
};

export const getClassJoinStatusData = (
  start: string,
  join?: number
): TClassJoinStatusData => {
  let data: TClassJoinStatusData = {
    status: 'Class Not Started',
  };

  if (start && moment(start) < moment()) {
    data = {
      status: 'Missed',
      color: '#FF5630',
      chipBg: '#FF5630',
      chipColor: TP_COLORS.white,
    };

    if (join) {
      if (
        moment(join).isBefore(start) ||
        moment(join).diff(moment(start), 'minutes') < 1
      ) {
        data = {
          status: 'On Time',
          color: '#3498DB',
          chipBg: '#3498DB',
          chipColor: TP_COLORS.white,
        };
      } else if (moment(join).diff(moment(start), 'minutes') >= 1) {
        data = {
          status: `${moment(join).diff(moment(start), 'minutes')} Min Late`,
          color: '#FFAB00',
          chipBg: '#FFAB00',
          chipColor: '#1E293B',
        };
      }
    }
  }

  return data;
};

export const checkSubjectCodeHide = (curriculumName: string): boolean =>
  curriculumName === 'Cambridge Lower Secondary' ||
  curriculumName === 'Edexcel Lower Secondary';

export const getClassShortUrlByWaitingId = (waitingId: string): string => {
  if (process.env.REACT_APP_SYNC_WITH_OLD_PLATFORM === 'true') {
    return `${process.env.REACT_APP_WAITING_ROOM_BASE_URL}/#/${waitingId}`;
  }

  return `${process.env.REACT_APP_BASE_URL}${process.env.PUBLIC_URL}/waiting-room/${waitingId}`;
};

export const scrubFileName = (fileName: string): string => {
  const fileNameArray = fileName
    .replace(/[/\\?%*#^&=+';:|"<>]/g, '')
    .replace(/\s/g, '')
    .split('.');
  const typeIndex = fileNameArray.length - 1;
  const fileType = fileNameArray[typeIndex];
  let extractedName = '';
  fileNameArray.forEach((name, index) => {
    if (index !== typeIndex) {
      extractedName += name;
    }
  });
  extractedName = cleanString(extractedName);
  extractedName += `_${new Date().getTime()}.${fileType}`;
  return extractedName;
};

const cleanString = (input: string) => {
  let output = '';
  for (let i = 0; i < input.length; i += 1) {
    if (input.charCodeAt(i) <= 127) {
      output += input.charAt(i);
    }
  }
  return output;
};

export const capitalizeFirstLetter = (word: string): string =>
  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

export const checkDiscountEligibility = (): boolean => {
  const country = getCountry();

  const eligibleCountries = [
    'Asia/Kuala_Lumpur',
    'Asia/Kuching',
    'Asia/Dubai',
    'Asia/Riyadh',
  ];

  return eligibleCountries.includes(country);
};

export const getFormatedTableLabels = ({
  labels,
  maxCount = 2,
}: {
  labels: string[];
  maxCount?: number;
}) => {
  if (labels.length <= maxCount) {
    return labels.join(', ');
  }

  const displayedLabels = labels.slice(0, maxCount);
  const remainingCount = labels.length - maxCount;

  return `${displayedLabels.join(', ')}... and ${remainingCount} more`;
};

export const calculateTotalAmountByCurrencyAndAmount = (
  amountData: { [key: string]: number },
  withSign = false
) => {
  let formatedAmount = '';

  if (amountData && !isEmpty(amountData)) {
    const currencies = Object.keys(amountData) || [];

    if (currencies.length) {
      currencies.forEach((currency, index) => {
        let sign = '';

        const amount = amountData[currency]
          ? parseFloat(amountData[currency].toString())
          : 0;

        if (withSign) {
          if (amount > 0) sign = '+';
          if (amount < 0) sign = '-';
        }

        if (amount) {
          formatedAmount += `${sign}${currency} ${amount.toFixed(2)}`;
        }

        if (currencies.length - 1 > index) {
          formatedAmount += ' / ';
        }
      });
    }
  }

  formatedAmount = formatedAmount.trim();

  if (formatedAmount.startsWith('/')) {
    formatedAmount = formatedAmount.substring(1).trim();
  }

  if (formatedAmount.endsWith('/')) {
    formatedAmount = formatedAmount
      .substring(0, formatedAmount.length - 1)
      .trim();
  }

  return formatedAmount;
};

export const isAiBuddy = (): boolean => {
  return window.location.href.includes('aibuddy');
};

// Helper function to derive filterData from filterSelections
export const deriveFilterDataFromSelections = (
  filterSelections: AdminCoursesFiltersData['filterSelections']
): {
  curriculum?: string;
  subject?: string;
  grade?: string;
  studentId?: string;
  tutorId?: string;
  status?: TCourseStatus;
  mode?: string;
} => {
  return {
    curriculum: filterSelections.selectedCurriculum?.value,
    subject: filterSelections.selectedSubject?.value,
    grade: filterSelections.selectedGrade?.value,
    studentId: filterSelections.selectedStudent?.value,
    tutorId: filterSelections.selectedTutor?.value,
    status: filterSelections.status?.value as TCourseStatus | undefined,
    mode: filterSelections.mode?.value,
  };
};
